'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import Image from 'next/image';
import { ChevronLeft, ChevronRight, ArrowRight, CheckCircle } from 'lucide-react';
import { useLocale } from '@/hooks/useLocale';
import { t } from '@/i18n/t';

interface Slide {
  title: string;
  accent: string;
  subtitle: string;
  cta: string;
  ctaHref: string;
  bgImage: string;
  bgAlt: string;
  price?: string;
  priceLabel?: string;
}

function getSlides(locale: 'sv' | 'en', threshold: string): Slide[] {
  return [
    {
      title: t(locale, 'hero.slide1Title'),
      accent: t(locale, 'hero.slide1Accent'),
      subtitle: t(locale, 'hero.slide1Subtitle'),
      cta: t(locale, 'hero.slide1Cta'),
      ctaHref: '/katalog',
      bgImage: '/images/hero/hero-workshop.webp',
      bgAlt: locale === 'sv' ? 'Bilverkstad med däck och verktyg — Autofrance reservdelsbutik' : 'Auto workshop with tires and tools — Autofrance parts store',
      priceLabel: t(locale, 'hero.slide1PriceLabel'),
    },
    {
      title: t(locale, 'hero.slide2Title'),
      accent: t(locale, 'hero.slide2Accent'),
      subtitle: t(locale, 'hero.slide2Subtitle'),
      cta: t(locale, 'hero.slide2Cta'),
      ctaHref: '/katalog/koppling',
      bgImage: '/images/hero/hero-french-cars.webp',
      bgAlt: locale === 'sv' ? 'Franska bilar — Peugeot, Renault och Citroën reservdelar' : 'French cars — Peugeot, Renault and Citroën parts',
      priceLabel: t(locale, 'hero.slide2PriceLabel'),
      price: t(locale, 'hero.slide2Price'),
    },
    {
      title: t(locale, 'hero.slide3Title'),
      accent: t(locale, 'hero.slide3Accent'),
      subtitle: t(locale, 'hero.slide3Subtitle'),
      cta: t(locale, 'hero.slide3Cta'),
      ctaHref: '/katalog/belysning',
      bgImage: '/images/hero/hero-mechanic.webp',
      bgAlt: locale === 'sv' ? 'Mekaniker som monterar bilbelysning — Autofrance bildelar' : 'Mechanic installing car lighting — Autofrance auto parts',
      priceLabel: t(locale, 'hero.slide3PriceLabel'),
      price: t(locale, 'hero.slide3Price'),
    },
    {
      title: t(locale, 'hero.slide4Title'),
      accent: t(locale, 'hero.slide4Accent'),
      subtitle: t(locale, 'hero.slide4Subtitle', { threshold }),
      cta: t(locale, 'hero.slide4Cta'),
      ctaHref: '/katalog/bromsskiva',
      bgImage: '/images/hero/hero-parts-closeup.webp',
      bgAlt: locale === 'sv' ? 'Bromsskivor och bromsbelägg på nära håll — köp bildelar online' : 'Brake discs and pads close-up — buy car parts online',
      priceLabel: t(locale, 'hero.slide4PriceLabel'),
      price: t(locale, 'hero.slide4Price'),
    },
  ];
}

export function HeroSlider({ threshold }: { threshold: string }) {
  const { locale } = useLocale();
  const slides = getSlides(locale, threshold);
  const [current, setCurrent] = useState(0);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const goTo = useCallback((n: number) => {
    setCurrent(((n % slides.length) + slides.length) % slides.length);
  }, [slides.length]);

  // Auto-advance
  useEffect(() => {
    const timer = setInterval(() => goTo(current + 1), 5000);
    return () => clearInterval(timer);
  }, [current, goTo]);

  // Swipe handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50;
    
    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        goTo(current + 1); // Swipe left = next
      } else {
        goTo(current - 1); // Swipe right = prev
      }
    }
  };

  const slide = slides[current];

  return (
    <section
      className="relative isolate h-[65vh] min-h-[420px] max-h-[600px] md:h-[55vh] md:min-h-[400px] overflow-hidden"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Background Images */}
      {slides.map((s, i) => (
        <div
          key={i}
          className="absolute inset-0 transition-opacity duration-700"
          style={{ opacity: i === current ? 1 : 0 }}
        >
          <Image
            src={s.bgImage}
            alt={s.bgAlt}
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 1920px"
            className="object-cover object-center"
            quality={90}
            priority={i === 0}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
        </div>
      ))}

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col justify-end pb-8 md:pb-16 px-5 md:px-10 max-w-5xl mx-auto">
        {/* Badge */}
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle className="w-4 h-4 text-green-400" />
          <span className="text-white/80 text-sm font-medium">{slide.subtitle}</span>
        </div>

        {/* Headline */}
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-3">
          {slide.title}
          <span className="text-amber-400"> {slide.accent}</span>
        </h1>

        {/* Price tag if available */}
        {slide.price && (
          <div className="flex items-baseline gap-2 mb-4">
            <span className="text-white/80 text-sm">{slide.priceLabel}</span>
            <span className="text-white text-2xl font-bold">{slide.price}</span>
          </div>
        )}
        {!slide.price && slide.priceLabel && (
          <p className="text-white/80 text-sm mb-4">{slide.priceLabel}</p>
        )}

        {/* CTA Button */}
        <a
          href={slide.ctaHref}
          className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold text-base px-8 py-4 rounded-xl w-full sm:w-auto transition-all active:scale-[0.98]"
          style={{ boxShadow: '0 4px 20px rgba(220,38,38,0.4)' }}
        >
          {slide.cta}
          <ArrowRight className="w-5 h-5" />
        </a>

        {/* Slide indicator - minimal line */}
        <div className="flex gap-1.5 mt-4">
          {slides.map((_, i) => (
            <div
              key={i}
              className={`h-1 rounded-full transition-all duration-300 ${
                i === current ? 'w-8 bg-white' : 'w-4 bg-white/30'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Navigation Arrows - DESKTOP ONLY */}
      <button
        onClick={() => goTo(current - 1)}
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm items-center justify-center hover:bg-white/20 transition-colors"
        aria-label={t(locale, 'hero.prevSlide')}
      >
        <ChevronLeft className="w-6 h-6 text-white" />
      </button>
      <button
        onClick={() => goTo(current + 1)}
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm items-center justify-center hover:bg-white/20 transition-colors"
        aria-label={t(locale, 'hero.nextSlide')}
      >
        <ChevronRight className="w-6 h-6 text-white" />
      </button>
    </section>
  );
}
